import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useOptionalToast } from './Toast';
import { reportsApi, datasetsApi } from '../lib/api';
import { buildReportElements, REPORT_TYPES } from '../lib/report-types';
import { nextElementPosition, tableColumns } from '../lib/report-layout';
import { peekReportHandoff, clearReportHandoff, handoffToReportState } from '../lib/report-handoff';
import {
  serializeTemplate, serializeElements, deserializeTemplate, maxElementId, templateTooLarge,
  readLocalTemplates, saveLocalTemplate, removeLocalTemplate,
} from '../lib/report-templates';

// Page sizes in mm → rendered at ~3.78 px/mm (96dpi)
const PAGE_SIZES = {
  a4: { label: 'A4', w: 210, h: 297 },
  letter: { label: 'Letter', w: 216, h: 279 },
  a3: { label: 'A3', w: 297, h: 420 },
};
const MM = 3.2; // px per mm on screen

const ELEMENT_TYPES = [
  { id: 'map', label: '🗺️ Map Snapshot' },
  { id: 'title', label: '🔤 Title' },
  { id: 'text', label: '📝 Text Block' },
  { id: 'legend', label: '🎨 Legend' },
  { id: 'table', label: '📋 Table' },
  { id: 'image', label: '🖼️ Image / Logo' },
];

let elId = 0;

/** Server detail (or message) for a failed template request. */
export function uploadErrorDetail(err) {
  const d = err?.response?.data?.detail;
  return String(typeof d === 'string' ? d : d ? JSON.stringify(d) : err?.message || 'request failed');
}

/**
 * ReportBuilder — drag-and-drop report layout with PDF export
 *
 * Props:
 *   getMapSnapshot() → dataURL (or a promise of one) of the current map canvas
 *   reportType   - summary | field | qc | anomaly: prefill a type-specific template
 *   project, resultLayers - used by the typed templates
 *   legendLayers - [{ name, color }] for the legend element
 *   tableRows    - array of property objects for the table element
 *   projectName
 *   onClose()
 */
export default function ReportBuilder({ getMapSnapshot, legendLayers = [], tableRows = [], projectName = 'Project', projectId, organizationId, reportType = null, project = null, resultLayers = [], onClose }) {
  const { C: themeC } = useTheme();
  const { showToast } = useOptionalToast();
  const C = { ...themeC, amber: themeC.warnText };
  // A composed print page sent from the Print Layout (lib/report-handoff.js) opens as the first element.
  const [handoff] = useState(() => handoffToReportState(peekReportHandoff(), { mm: MM, pageSizes: PAGE_SIZES, nextId: () => `el-${++elId}` }));
  useEffect(() => { clearReportHandoff(); }, []);
  const [pageSize, setPageSize] = useState(handoff?.pageSize || 'a4');
  const [landscape, setLandscape] = useState(handoff ? handoff.landscape : true);
  const [elements, setElements] = useState(handoff?.elements || []);
  const [typedState, setTypedState] = useState(null); // 'loading' while a typed template is prefilled
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [serverTemplates, setServerTemplates] = useState(null); // null = list closed
  const [localTemplates, setLocalTemplates] = useState(() => readLocalTemplates());
  const [tplBusy, setTplBusy] = useState(false);
  const [tplMsg, setTplMsg] = useState('');
  const pageRef = useRef(null);
  const dragRef = useRef(null);

  const size = PAGE_SIZES[pageSize];
  const pageW = (landscape ? size.h : size.w) * MM;
  const pageH = (landscape ? size.w : size.h) * MM;

  // Typed report (Reports > Project Reports): prefill once, after the project's datasets and the map are in.
  const prefilled = useRef(false);
  const latest = useRef({});
  latest.current = { getMapSnapshot, projectName, project, resultLayers, pageW, pageH };
  useEffect(() => {
    if (!reportType || !REPORT_TYPES.includes(reportType) || handoff || prefilled.current) return undefined;
    prefilled.current = true;
    let active = true;
    setTypedState('loading');
    (async () => {
      let datasets = [];
      if (projectId) {
        try {
          const res = await datasetsApi.list({ project_id: projectId, page_size: 200 });
          datasets = res.data?.datasets || [];
        } catch (err) {
          setTplMsg(`Could not load project datasets: ${err?.response?.data?.detail || err?.message || 'request failed'}`);
        }
      }
      let mapSnapshot = null;
      if (reportType !== 'qc') {
        try { mapSnapshot = (await latest.current.getMapSnapshot?.()) || null; } catch { /* no map */ }
      }
      if (!active) return;
      setElements(buildReportElements(reportType, { ...latest.current, datasets, mapSnapshot, nextId: () => `el-${++elId}` }));
      setTypedState(null);
    })();
    return () => { active = false; prefilled.current = false; };
  }, [reportType, projectId, handoff]);

  const addElement = async (type) => {
    const id = `el-${++elId}`;
    // Elements stack below the existing ones so nothing overlaps by default (V.28)
    const pos = nextElementPosition(elements);
    const base = { id, type, ...pos };
    let el;
    switch (type) {
      case 'map': {
        const snapshot = await getMapSnapshot?.();
        el = { ...base, w: pageW * 0.6, h: pageH * 0.5, src: snapshot };
        break;
      }
      case 'title':
        el = { ...base, w: pageW * 0.7, h: 44, text: projectName, fontSize: 24, bold: true };
        break;
      case 'text':
        el = { ...base, w: pageW * 0.4, h: 90, text: 'Double-click to edit this text block…', fontSize: 11 };
        break;
      case 'legend':
        el = { ...base, w: 170, h: Math.max(60, legendLayers.length * 20 + 30) };
        break;
      case 'table':
        el = { ...base, w: pageW * 0.45, h: 140 };
        break;
      case 'image':
        el = { ...base, w: 140, h: 80, src: null };
        break;
      default:
        return;
    }
    setElements(prev => [...prev, el]);
    setSelectedId(id);
  };

  const updateEl = (id, patch) => setElements(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  const removeEl = (id) => { setElements(prev => prev.filter(e => e.id !== id)); setSelectedId(null); };

  // Drag / resize
  const startDrag = (e, id, mode) => {
    e.stopPropagation();
    e.preventDefault();
    const el = elements.find(x => x.id === id);
    dragRef.current = { id, mode, startX: e.clientX, startY: e.clientY, ox: el.x, oy: el.y, ow: el.w, oh: el.h };
    setSelectedId(id);
  };

  const onMouseMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (d.mode === 'move') {
      updateEl(d.id, { x: Math.max(0, d.ox + dx), y: Math.max(0, d.oy + dy) });
    } else {
      updateEl(d.id, { w: Math.max(40, d.ow + dx), h: Math.max(24, d.oh + dy) });
    }
  }, []);

  const onMouseUp = useCallback(() => { dragRef.current = null; }, []);

  const handleImageUpload = (id, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updateEl(id, { src: ev.target.result });
    reader.readAsDataURL(file);
  };

  // Templates
  const saveTemplate = () => {
    const tpl = { pageSize, landscape, elements: serializeElements(elements) };
    const blob = new Blob([JSON.stringify(tpl, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = 'report-template.json'; link.click();
    URL.revokeObjectURL(url);
  };

  const applyTemplate = async (row) => {
    const tpl = deserializeTemplate(row);
    setPageSize(tpl.pageSize);
    setLandscape(tpl.landscape);
    // Keep the module-level id counter ahead of loaded ids (avoid duplicate keys).
    elId = Math.max(elId, maxElementId(tpl.elements));
    // Refresh map snapshots on load
    const snapshot = tpl.elements.some(e => e.type === 'map') ? await getMapSnapshot?.() : null;
    setElements(tpl.elements.map(e => e.type === 'map' ? { ...e, src: snapshot } : e));
    setSelectedId(null);
  };

  const loadTemplate = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try { applyTemplate(JSON.parse(ev.target.result)); } catch { /* invalid template */ }
    };
    reader.readAsText(file);
  };

  // Server templates (org-scoped, /api/reports). Browser-local storage stays as the fallback.
  const canUseServer = !!(projectId || organizationId);
  const scopeFields = projectId ? { project_id: projectId } : { organization_id: organizationId };

  const withBusy = async (fn) => {
    setTplBusy(true); setTplMsg('');
    try { await fn(); } catch (err) {
      setTplMsg(String(err?.response?.data?.detail || err?.message || 'Request failed'));
    } finally { setTplBusy(false); }
  };

  const refreshServerList = () => reportsApi.list(projectId ? { project_id: projectId } : { organization_id: organizationId })
    .then(res => setServerTemplates(res.data || []));

  const saveLocally = (name) => {
    const ok = saveLocalTemplate(name, { pageSize, landscape, elements });
    setLocalTemplates(readLocalTemplates());
    return ok;
  };

  const saveToServer = () => withBusy(async () => {
    const name = (window.prompt('Template name:', `${projectName} report`) || '').trim();
    if (!name) return;
    const body = serializeTemplate({ pageSize, landscape, elements });
    if (templateTooLarge(body.template)) {
      setTplMsg(`Template is over 512 KB (large images?); ${saveLocally(name) ? 'saved locally instead' : 'not saved'}.`);
      return;
    }
    try {
      await reportsApi.create({ ...body, name, ...scopeFields });
      setTplMsg(`Saved "${name}" to the server.`);
      if (serverTemplates) await refreshServerList();
    } catch (err) {
      // Offline / no permission: keep the work in this browser rather than lose it.
      if (!saveLocally(name)) throw err;
      setTplMsg(`Server save failed (${err?.response?.status || 'offline'}); saved "${name}" locally instead.`);
    }
  });

  const openServerList = () => withBusy(refreshServerList);

  const loadFromServer = (id) => withBusy(async () => {
    const res = await reportsApi.get(id);
    applyTemplate(res.data);
    setServerTemplates(null);
    setTplMsg(`Loaded "${res.data.name}".`);
  });

  const deleteFromServer = (t) => {
    if (!window.confirm(`Delete template "${t.name}" from the server?`)) return;
    withBusy(async () => { await reportsApi.remove(t.id); await refreshServerList(); });
  };

  const uploadLocalTemplates = () => withBusy(async () => {
    const local = readLocalTemplates();
    let done = 0;
    const failures = [];
    for (const t of local) {
      try {
        await reportsApi.create({ name: t.name, template: t.template, page_size: t.page_size, orientation: t.orientation, ...scopeFields });
        removeLocalTemplate(t.id); done += 1;
      } catch (err) { // a failed one stays local
        failures.push(`"${t.name}": ${uploadErrorDetail(err)}`);
      }
    }
    setLocalTemplates(readLocalTemplates());
    setTplMsg(`Uploaded ${done} of ${local.length} local template(s).${failures.length ? ` Failed - ${failures.join('; ')}` : ''}`);
    failures.forEach((f) => showToast?.(`Template upload failed: ${f}`, 'error', 6000));
    if (serverTemplates) await refreshServerList();
  });

  const exportPDF = async () => {
    setExporting(true);
    setSelectedId(null);
    try {
      const [{ default: html2canvas }, { default: JsPDF }] = await Promise.all([
        import('html2canvas'), import('jspdf'),
      ]);
      await new Promise(r => setTimeout(r, 60)); // let selection outlines clear
      const canvas = await html2canvas(pageRef.current, { scale: 2, backgroundColor: '#ffffff' });
      const pdf = new JsPDF({
        orientation: landscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: pageSize === 'letter' ? 'letter' : pageSize,
      });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.85), 'JPEG', 0, 0, pdfW, pdfH);
      pdf.save(`${projectName.replace(/[^\w-]+/g, '_')}_report.pdf`);
    } finally {
      setExporting(false);
    }
  };

  const selected = elements.find(e => e.id === selectedId);

  const renderElement = (el) => {
    const isSel = el.id === selectedId;
    const frame = {
      position: 'absolute', left: el.x, top: el.y, width: el.w, height: el.h,
      border: isSel ? '1.5px dashed #00b8de' : '1px solid transparent',
      cursor: 'move', overflow: 'hidden', boxSizing: 'border-box',
    };
    let content = null;
    switch (el.type) {
      case 'map':
      case 'image':
        content = el.src
          ? <img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: el.type === 'map' ? 'cover' : 'contain' }} draggable={false} />
          : <div style={{ width: '100%', height: '100%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#6e7681' }}>
              {el.type === 'map' ? 'Map snapshot unavailable' : 'Select the element, then upload an image'}
            </div>;
        break;
      case 'title':
      case 'text':
        content = (
          <div style={{ width: '100%', height: '100%', fontSize: el.fontSize, fontWeight: el.bold ? 700 : 400, color: '#11161d', lineHeight: 1.4, whiteSpace: 'pre-wrap', padding: 2 }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              const text = window.prompt('Edit text:', el.text);
              if (text !== null) updateEl(el.id, { text });
            }}>
            {el.text}
          </div>
        );
        break;
      case 'legend':
        content = (
          <div style={{ padding: 8, fontSize: 10, color: '#11161d' }}>
            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11 }}>Legend</div>
            {legendLayers.length === 0 && <div style={{ color: '#8b949e' }}>No visible layers</div>}
            {legendLayers.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, display: 'inline-block', border: '1px solid rgba(0,0,0,0.15)' }} />
                <span>{l.name}</span>
              </div>
            ))}
          </div>
        );
        break;
      case 'table': {
        const rowsData = Array.isArray(el.rows) ? el.rows : tableRows;
        const cols = el.columns || (rowsData.length ? tableColumns(rowsData, 6) : []);
        content = (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 8, color: '#11161d' }}>
            <thead>
              <tr>{cols.map(c => <th key={c} style={{ border: '1px solid #c9d1d9', padding: '2px 4px', background: '#e6edf3', textAlign: 'left' }}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {rowsData.slice(0, el.rows ? 30 : 10).map((r, i) => (
                <tr key={i}>{cols.map(c => <td key={c} style={{ border: '1px solid #e2e8f0', padding: '2px 4px' }}>{String(r[c] ?? '')}</td>)}</tr>
              ))}
              {rowsData.length === 0 && <tr><td style={{ padding: 6, color: '#8b949e' }}>No layer data</td></tr>}
            </tbody>
          </table>
        );
        break;
      }
      default:
        content = null;
    }
    return (
      <div key={el.id} style={frame} onMouseDown={(e) => startDrag(e, el.id, 'move')}>
        {content}
        {isSel && (
          <div onMouseDown={(e) => startDrag(e, el.id, 'resize')}
            style={{ position: 'absolute', right: -1, bottom: -1, width: 12, height: 12, background: '#00b8de', cursor: 'nwse-resize', borderRadius: 2 }} />
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg0, zIndex: 200, display: 'flex', flexDirection: 'column' }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
      {/* Header */}
      <div style={{ height: 48, background: C.bg1, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text0 }}>📄 Report Builder{typedState === 'loading' ? ' (preparing report…)' : ''}</span>
        <select value={pageSize} onChange={e => setPageSize(e.target.value)}
          style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
          {Object.entries(PAGE_SIZES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <button onClick={() => setLandscape(v => !v)}
          style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>
          {landscape ? '▭ Landscape' : '▯ Portrait'}
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={saveTemplate} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>💾 Save Template</button>
        <label style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>
          📂 Load
          <input type="file" accept=".json" style={{ display: 'none' }} onChange={e => loadTemplate(e.target.files?.[0])} />
        </label>
        <button onClick={saveToServer} disabled={tplBusy || !canUseServer} title={canUseServer ? 'Save to the server (shared with your organization)' : 'Open a project to save to the server'}
          style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>☁ Save to server</button>
        <button onClick={openServerList} disabled={tplBusy || !canUseServer}
          style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>☁ Load from server</button>
        {localTemplates.length > 0 && (
          <button onClick={uploadLocalTemplates} disabled={tplBusy || !canUseServer}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.amber, cursor: 'pointer' }}>
            ⬆ Upload local templates ({localTemplates.length})
          </button>
        )}
        <button onClick={exportPDF} disabled={exporting || elements.length === 0}
          style={{ fontSize: 12, padding: '5px 14px', borderRadius: 6, background: 'linear-gradient(135deg,#00b8de,#3b82f6)', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: (exporting || elements.length === 0) ? 0.5 : 1 }}>
          {exporting ? '⏳ Exporting…' : '📑 Export PDF'}
        </button>
        <button onClick={onClose} style={{ fontSize: 14, background: 'none', border: 'none', color: C.text3, cursor: 'pointer' }}>✕</button>
      </div>

      {(tplMsg || serverTemplates) && (
        <div style={{ background: C.bg1, borderBottom: `1px solid ${C.border}`, padding: '6px 16px', fontSize: 11, color: C.text1, flexShrink: 0 }}>
          {tplMsg && <div style={{ marginBottom: serverTemplates ? 6 : 0 }}>{tplMsg}</div>}
          {serverTemplates && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginBottom: 4 }}>
                <span>Server templates</span>
                <button onClick={() => setServerTemplates(null)} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer' }}>✕</button>
              </div>
              {serverTemplates.length === 0 && <div style={{ color: C.text3 }}>No templates saved yet.</div>}
              {serverTemplates.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ flex: 1 }}>{t.name} <span style={{ color: C.text3 }}>({t.page_size}, {t.orientation}{t.project_id ? '' : ', org-wide'})</span></span>
                  <button onClick={() => loadFromServer(t.id)} disabled={tplBusy} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>Load</button>
                  <button onClick={() => deleteFromServer(t)} disabled={tplBusy} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', color: C.red, cursor: 'pointer' }}>Delete</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Palette */}
        <div style={{ width: 180, background: C.bg1, borderRight: `1px solid ${C.border}`, padding: 10, flexShrink: 0, overflow: 'auto' }}>
          <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>Add Element</div>
          {ELEMENT_TYPES.map(t => (
            <button key={t.id} onClick={() => addElement(t.id)}
              style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 11, padding: '7px 10px', marginBottom: 4, borderRadius: 6, background: C.bg3, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}

          {/* Selected element properties */}
          {selected && (
            <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Selected: {selected.type}</div>
              {(selected.type === 'text' || selected.type === 'title') && (
                <>
                  <label style={{ fontSize: 9, color: C.text3 }}>Font size — {selected.fontSize}px</label>
                  <input type="range" min={8} max={48} value={selected.fontSize}
                    onChange={e => updateEl(selected.id, { fontSize: parseInt(e.target.value) })}
                    style={{ width: '100%', accentColor: C.cyan }} />
                </>
              )}
              {selected.type === 'image' && (
                <label style={{ display: 'block', fontSize: 10, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer', textAlign: 'center', marginBottom: 6 }}>
                  Upload image…
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleImageUpload(selected.id, e.target.files?.[0])} />
                </label>
              )}
              {selected.type === 'map' && (
                <button onClick={() => updateEl(selected.id, { src: getMapSnapshot?.() })}
                  style={{ width: '100%', fontSize: 10, padding: '5px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer', marginBottom: 6 }}>
                  ↻ Refresh snapshot
                </button>
              )}
              <button onClick={() => removeEl(selected.id)}
                style={{ width: '100%', fontSize: 10, padding: '5px', borderRadius: 6, background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', color: C.red, cursor: 'pointer' }}>
                🗑 Delete element
              </button>
            </div>
          )}
        </div>

        {/* Page canvas */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24 }}
          onMouseDown={() => setSelectedId(null)}>
          <div ref={pageRef}
            onMouseDown={e => e.stopPropagation()}
            style={{ width: pageW, height: pageH, background: '#ffffff', position: 'relative', boxShadow: '0 8px 40px rgba(0,0,0,0.5)', flexShrink: 0 }}>
            {elements.map(renderElement)}
            {elements.length === 0 && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b949e', fontSize: 13 }}>
                Add elements from the left palette, drag to arrange, then Export PDF.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
